import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { GitBranch } from "lucide-react";
import { analyticsAPI } from "../services/analyticsApi";
import "./DashboardPipelineFunnel.css";

interface FunnelStage {
  id: string;
  name: string;
  color: string;
  count: number;
  conversionRate?: number;
}

interface FunnelResponse {
  success: boolean;
  data: {
    stages: FunnelStage[];
    totalApplications: number;
  };
}

const FunnelSkeleton: React.FC = () => (
  <div className="db-funnel__skeleton">
    <div className="db-funnel__skeleton-bar" />
    <div className="db-funnel__skeleton-labels">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="db-funnel__skeleton-label" />
      ))}
    </div>
  </div>
);

const DashboardPipelineFunnel: React.FC = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery<FunnelResponse>(
    ["dashboard-funnel"],
    () => analyticsAPI.getFunnel({ range: "30d" }),
    { staleTime: 300000 },
  );

  const stages = data?.data?.stages || [];
  const total = stages.reduce((sum, s) => sum + s.count, 0);

  const handleSegmentClick = (stageId?: string) => {
    navigate(stageId ? `/pipeline?status=${stageId}` : "/pipeline");
  };

  return (
    <div className="dashboard-section-full db-funnel">
      <div className="dashboard-section-header">
        <h3>{t("dashboard.pipelineFunnel", "Pipeline Funnel")}</h3>
        <button
          className="view-all-link"
          onClick={() => navigate("/pipeline")}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 0,
          }}
        >
          {t("actions.viewAll")} →
        </button>
      </div>

      {isLoading && <FunnelSkeleton />}

      {error && (
        <div className="db-funnel__error">
          <span>{t("errors.loadingFailed", "Failed to load funnel data")}</span>
          <button
            className="view-all-link"
            onClick={() => navigate("/pipeline")}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
            }}
          >
            {t("dashboard.viewPipeline", "View Pipeline")} →
          </button>
        </div>
      )}

      {!isLoading && !error && total === 0 && (
        <div className="db-funnel__empty">
          <GitBranch size={24} className="db-funnel__empty-icon" />
          <p>
            {t("dashboard.noFunnelData", "No applications in the last 30 days")}
          </p>
          <button
            className="view-all-link"
            onClick={() => navigate("/pipeline")}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
            }}
          >
            {t("dashboard.viewPipeline", "View Pipeline")} →
          </button>
        </div>
      )}

      {!isLoading &&
        !error &&
        total > 0 &&
        (() => {
          // Normalize widths: apply minimum then scale to sum to 100%
          const visibleStages = stages.filter((s) => s.count > 0);
          const rawWidths = visibleStages.map((s) =>
            Math.max(3, (s.count / total) * 100),
          );
          const totalWidth = rawWidths.reduce((a, b) => a + b, 0);
          const normalizedWidths = rawWidths.map((w) => (w / totalWidth) * 100);

          return (
            <>
              <div className="db-funnel__bar">
                {visibleStages.map((stage, idx) => (
                  <button
                    key={stage.id}
                    className="db-funnel__segment"
                    style={{
                      width: `${normalizedWidths[idx]}%`,
                      backgroundColor: stage.color || "#1E40AF",
                    }}
                    onClick={() => handleSegmentClick(stage.id)}
                    title={`${stage.name}: ${stage.count} applications`}
                    aria-label={`${stage.name}: ${stage.count} applications`}
                  >
                    <span className="db-funnel__segment-count">
                      {stage.count}
                    </span>
                  </button>
                ))}
              </div>
              <div className="db-funnel__labels">
                {stages
                  .filter((s) => s.count > 0)
                  .map((stage) => (
                    <div key={stage.id} className="db-funnel__label">
                      <span
                        className="db-funnel__label-dot"
                        style={{ backgroundColor: stage.color || "#1E40AF" }}
                      />
                      <span className="db-funnel__label-name">
                        {stage.name}
                      </span>
                      <span className="db-funnel__label-count">
                        {stage.count}
                      </span>
                    </div>
                  ))}
              </div>
              <div className="db-funnel__total">
                {total} {t("dashboard.totalInPipeline", "total in pipeline")}
              </div>
            </>
          );
        })()}
    </div>
  );
};

export default DashboardPipelineFunnel;
